import { BODY_TYPE, FRAMES, FRAME_VARIANTS, ICON_SIZES, SPACING, pixelType, type FrameName, type FrameVariantName, type Sides } from "odyssey-shared";

/**
 * The kit's measurements (ADR-061, amendment "the design system lands"): every size a component sets is a
 * whole number of logical pixels derived here from the token table, the frames' insets, the pixel face's
 * measured line height, the icon roles and the spacing steps, never from the export's numbers. A component
 * writes each one as `calc(N * var(--px))` through `logical`, so one logical pixel is always a whole number
 * of device pixels, and a token that moves (a measured font size, an icon role moving to its purchased set
 * in P1.9-T20) moves every size built on it.
 */

/**
 * The checkbox tick's size in logical pixels: the grid `art/marks/check/tick_6.grid`, whose atlas name
 * carries it (`ui/check/tick_6`).
 */
export const TICK_SIZE = 6;

/**
 * The kit's small glyphs (ADR-061's amendment "the kit and the entry forms match the design"), each with its size in
 * logical pixels, the size its grid `art/marks/glyph/<name>_<size>.grid` is drawn at: the field's error mark, the
 * Retry button's reload, a list row's person, character select's standing figure, the game menu's play, sliders and
 * logout, the player card's in-combat mark, the server notice's clock, unlink, hourglass and check (ADR-078), and the
 * ability bar's and status rows' marks (P2.2-T4): Strike's bullseye at 16, the stun's zap and Resurrection Sickness's
 * hourglass at 12, the level-up toast's rising line at 12 (P2.4-T4), and the safe passage's shield at 12 (ADR-076's amendment
 * of 2026-10-03), and a password field's eye and closed eye at 12 (ADR-061's amendment "the password is typed twice
 * to create an account, and can be shown"), and the Inventory window's drop, the potion's glyph, and briefcase, its
 * empty state's, at 16, and the game menu's Inventory item's briefcase at 8 (P2.8-T11, ADR-091 decision 21), and the chat entry row's Send arrow at 12 (P1.9-T40), and the minimised chat
 * bar's marks (P1.9-T30): the minimise control's bar at 8, the open control's chevron and the bar's Send arrow at 6,
 * and the chat glyph at 8, and the discovery toast's folded map at 12 (P2.11-T8), and the Look panel's turn chevrons at 12
 * and Randomise's dice at 8 (P2.10-T28), and the game menu's Change look item's standing figure at 8 (P2.10-T29),
 * and the emote wheel's page markers at 10 (P2.19-T11): heart, users, tournament, user-medium, human-run-medium and
 * face, and the pending spinner's still ring and its ten turning frames at 8 (P2.8-T41), and the item menu's Consume
 * drop and Split stack grid at 8 (P2.8-T47, ADR-125 decision 7.1), the wallet line's coin at 12, the design's
 * `CurrencyMark` size, drawn in each coin's colour where its art is not (P2.8-T38), and the Inventory window's tool row
 * (P2.8-T45, ADR-125 decision 4.2): the search field's magnifier and clear cross at 12 and the Filter and Sort buttons'
 * caret at 8, and the wallet line's stand-in currencies at 12 where their art is not (P2.8-T59): trophy (Honor), book
 * (Marks of the Lyceum) and script-text (Inscription Tokens), and the nameplate's party mark, the circle's fill at 6
 * (P2.13-T22, ADR-101 decision 24).
 */
export const GLYPH_SIZES = { alert: 8, reload: 12, user: 12, "user-small": 8, "user-plus": 12, human: 12, play: 8, sliders: 8, logout: 8, zap: 8, lock: 8, clock: 8, unlink: 8, hourglass: 8, check: 8, bullseye: 16, "human-run": 16, sword: 16, sheathed: 16, "zap-large": 12, "hourglass-large": 12, "trending-up": 12, venom: 12, passage: 12, eye: 12, "eye-closed": 12, drop: 16, briefcase: 16, "briefcase-small": 8, "arrow-right": 12, "arrow-right-small": 6, minus: 8, plus: 8, "chevron-up": 6, chat: 8, map: 12, "chevron-left": 12, "chevron-right": 12, dice: 8, "human-small": 8, heart: 10, users: 10, "party-dot": 6, tournament: 10, "user-medium": 10, "human-run-medium": 10, face: 10, shield: 8, "chat-off": 8, "eye-small": 8, "info-box": 8, "close-small": 8, "warning-box": 12, "user-x": 12, note: 12, "users-large": 12, spinner: 8, "spinner-1": 8, "spinner-2": 8, "spinner-3": 8, "spinner-4": 8, "spinner-5": 8, "spinner-6": 8, "spinner-7": 8, "spinner-8": 8, "spinner-9": 8, "spinner-10": 8, "drop-small": 8, grid: 8, coin: 12, trophy: 12, book: 12, "script-text": 12, search: 12, close: 12, "chevron-down": 8, star: 8, bookmark: 8, skull: 8, float: 8, dock: 8, popout: 8, popin: 8 } as const;

/**
 * A CSS length of whole logical pixels.
 *
 * @param n Logical pixels.
 * @returns `calc(n * var(--px))`.
 * @throws RangeError when `n` is not a whole number, which would put a box between device pixels.
 * @example
 * logical(18);
 * // "calc(18 * var(--px))"
 */
export function logical(n: number): string {
  if (!Number.isInteger(n)) {
    throw new RangeError(`${n} is not a whole number of logical pixels`);
  }
  return `calc(${n} * var(--px))`;
}

/**
 * Where a box starts when set in the middle of a larger one, rounded down to a whole logical pixel, so a
 * box one pixel narrower than an even gap sits a pixel high or left rather than between pixels.
 *
 * @param outer The larger box's size in logical pixels.
 * @param inner The box's size in logical pixels.
 * @returns floor((outer - inner) / 2).
 * @throws RangeError when either size is not a whole number.
 * @example
 * centreOffset(13, 10);
 * // 1
 */
export function centreOffset(outer: number, inner: number): number {
  if (!Number.isInteger(outer) || !Number.isInteger(inner)) {
    throw new RangeError(`centring ${inner} in ${outer} needs whole logical pixels`);
  }
  return Math.floor((outer - inner) / 2);
}

/**
 * Where a laid-out box starts when set in the middle of a measured area, as a whole number of logical
 * pixels from the area's edge. The area may be a fraction of a logical pixel wide (a window whose device
 * size is not a multiple of the UI scale), the box never is, and a box larger than the area starts at its
 * edge.
 *
 * @param outer The area's size in logical pixels, measured.
 * @param inner The box's size in logical pixels, measured; rounded to the whole pixels it is laid out in.
 * @returns max(0, floor((outer - inner) / 2)).
 * @example
 * placeCentred(640.5, 200);
 * // 220
 */
export function placeCentred(outer: number, inner: number): number {
  return Math.max(0, Math.floor((outer - Math.round(inner)) / 2));
}

/**
 * A spacing step of the token table, checked at compile time, so padding and gaps come from the table.
 *
 * @param step One of `SPACING`.
 * @returns The step in logical pixels.
 */
function space(step: (typeof SPACING)[number]): number {
  return step;
}

/**
 * A frame's insets: its own when the export declares it, its base frame's when it is one of the kit's
 * variants, which are painted on their base's insets.
 *
 * @param frame The frame or variant.
 * @returns Its insets on each side.
 */
function insetsOf(frame: FrameName | FrameVariantName): Sides {
  return Object.hasOwn(FRAMES, frame) ? FRAMES[frame as FrameName].insets : FRAMES[FRAME_VARIANTS[frame as FrameVariantName].base].insets;
}

/**
 * A size that holds content of a given height inside a frame's top and bottom insets.
 *
 * @param frame The frame.
 * @param content The content's height in logical pixels.
 * @returns The frame's outer height.
 */
function framedHeight(frame: FrameName | FrameVariantName, content: number): number {
  return insetsOf(frame).top + content + insetsOf(frame).bottom;
}

/**
 * A size that holds content of a given width inside a frame's left and right insets.
 *
 * @param frame The frame.
 * @param content The content's width in logical pixels.
 * @returns The frame's outer width.
 */
function framedWidth(frame: FrameName | FrameVariantName, content: number): number {
  return insetsOf(frame).left + content + insetsOf(frame).right;
}

const BUTTON_LINE = pixelType("button").lineHeight;
const LABEL_LINE = pixelType("label").lineHeight;
const TITLE_LINE = pixelType("panel-title").lineHeight;
const FIELD_PADDING_Y = space(2);
const CHECK_BOX = framedWidth("field", TICK_SIZE);
const CHECK_ROW = Math.max(CHECK_BOX, LABEL_LINE);
const ICON_BUTTON_HEIGHT = framedHeight("button", ICON_SIZES.button);
const NOTE_LINE = Math.max(GLYPH_SIZES.alert, BODY_TYPE.caption.lineHeight);
const FIELD_HEAD = LABEL_LINE;
const FIELD_BOX = framedHeight("field", FIELD_PADDING_Y + BODY_TYPE.field.lineHeight + FIELD_PADDING_Y);
const FIELD_INNER = FIELD_BOX - insetsOf("field").top - insetsOf("field").bottom;
const FIELD_HEAD_GAP = space(1);
const FIELD_NOTE_GAP = space(2);
const WINDOW_TITLE_ROW = space(1) + LABEL_LINE + space(1);
const WINDOW_HEADER = Math.max(LABEL_LINE, ICON_BUTTON_HEIGHT);
const DIALOG_HEADER = Math.max(TITLE_LINE, ICON_BUTTON_HEIGHT);
const MENU_ITEM = space(1) + LABEL_LINE + space(1);
const MENU_PADDING_X = space(4);
const MENU_GLYPH_GAP = space(4);
const KEY_HEIGHT = framedHeight("button", LABEL_LINE);
const ROW_EDGE = space(1);
const ROW_PADDING = space(1);
const ROW_NAME = BODY_TYPE.body.lineHeight;
const ROW_SUB = BODY_TYPE["body-sm"].lineHeight;
const ROW_SUB_GAP = space(2);
const ROW_GLYPH = GLYPH_SIZES.user;
const LIST_ROW = ROW_EDGE + ROW_PADDING + ROW_NAME + ROW_PADDING + ROW_EDGE;
const BADGE_HEIGHT = 1 + LABEL_LINE + 1;
const MESSAGE_PADDING_X = space(4);
const MESSAGE_PADDING_Y = space(1);
const MESSAGE_LINE = framedHeight("notice", MESSAGE_PADDING_Y + BODY_TYPE.body.lineHeight + MESSAGE_PADDING_Y);
const BAR_FILL = space(4);
const BAR_TRACK = framedHeight("bar-track", BAR_FILL);
const BAR_INSETS = framedWidth("bar-track", 0);
/**
 * The flag marks' sizes (the atlas's `ui/flag/<shape>_<size>`): 16 for the HUD, 8 for nameplates.
 */
export const FLAG_SIZES = [16, 8] as const;

/**
 * A flag mark's size in logical pixels.
 */
export type FlagSize = (typeof FLAG_SIZES)[number];

/**
 * The lock a locked flag mark carries (`art/marks/lock_8.grid`), and how far it overlaps the mark's
 * bottom-right corner on each axis, as `scripts/ui-atlas.ts` composes the locked variants: a locked mark
 * is its size plus the lock's less the overlap on each side.
 */
export const FLAG_LOCK = { size: 8, overlap: 4 } as const;

/**
 * How a flag mark and its word sit in a row: the box holds the locked variant, so locking a mark moves
 * nothing beside it, with the mark in its top left, where the locked variant draws it; the mark and the
 * label's line are set in the middle of the taller of the two, rounded down.
 *
 * @param size The mark's size.
 * @returns The box's size, the row's height, the mark's and the word's offsets from the row's top, and the
 *   gap before the word.
 */
function flagRow(size: FlagSize): { readonly box: number; readonly height: number; readonly markTop: number; readonly wordTop: number; readonly gap: number } {
  const box = size + FLAG_LOCK.size - FLAG_LOCK.overlap;
  const core = Math.max(size, LABEL_LINE);
  const markTop = centreOffset(core, size);
  const wordTop = centreOffset(core, LABEL_LINE);
  return { box, height: Math.max(markTop + box, wordTop + LABEL_LINE), markTop, wordTop, gap: space(3) };
}

/**
 * The reading panel's size in logical pixels, the same for every text (P1.9-T37, ADR-077 decision 1): the export's
 * fixed 240 by 160, narrowed so that in the middle of the 640 by 360 grid it clears the chat slot beside it and the
 * message line under it. The place is one constant for the author to move (ADR-061's amendment "the reading panel").
 */
const READING_WIDTH = 232;
const READING_HEIGHT = 160;

const BUSY_DOTS = 3;
const BUSY_WIDTH = BUSY_DOTS * space(2) + (BUSY_DOTS - 1) * space(2);

/**
 * Every derived size of the kit, in logical pixels.
 *
 * - A button is its frame's top inset, the button role's line and its bottom inset tall; an icon button
 *   holds a button-role icon inside the same frame. Busy shows three dots, two pixels square with two
 *   between, in the middle of the label's line or the icon, rounded down to whole pixels; on a busy menu
 *   item they start where its label starts, set down the middle of its label's line (`menuItemLayout`).
 * - A text field is its label's line, a pixel, and its box: the box is its frame's insets around the field
 *   role's line and two pixels of padding above and below it, so a field with no note is 13 + 1 + 18 = 32
 *   (`height`). A note line, only when there is a hint or an error, adds `noteGap` and a line as tall as the
 *   taller of the 8 px alert glyph and the caption's line, with the caption set in its middle. A password
 *   field's show or hide button (`reveal`) is a square as tall as the box inside its frame, 14, holding the
 *   12 px eye in its middle, `right` from the frame so the eye ends where the box's padding does. The search
 *   form's box has no label line; its magnifier, input, count and clear cross stand `search.gap` apart (the
 *   design's 3), the cross in the reveal button's square.
 * - A checkbox's box is the field frame around the tick; a row is as tall as the taller of the box and the
 *   label role's line, each set in the middle of it.
 * - A key glyph is the button's frame around the label role's line.
 * - A window's title bar is its title's line with a pixel above and below it (`titleRow`), or, when it has a
 *   close button, as tall as the taller of its title's line and that button (`header`); a dialog's header is
 *   its title's line (`title`), or with a close button the taller of that line and the button (`header`).
 * - A message line of one line is the notice frame around the body role's line and a pixel above and below
 *   it; each further line adds the body role's line. `chromeX` is what its frame and padding add to the
 *   width of its text.
 * - A menu item is the label role's line with a pixel above and below it, or as tall as a key glyph in a menu
 *   whose items carry a key hint (`menuItemLayout`); an item's glyph sits `glyphGap` before its label, and a
 *   divider between items keeps `dividerMargin` above and below it. A list row is its content, a pixel
 *   of padding and a one-pixel edge on each side, the edge showing only when the row is selected, so
 *   selecting a row changes no size: 14 for a name in the body role alone (`row`), taller for a 12 px glyph
 *   (`rowHeight`) or a sub-line under the name. A section heading is the label role's line with padding.
 * - A bar's fill is four rows (the highlight, two flat rows and the shade) and its track the bar track
 *   frame's one-pixel edge around them, 6 in all; `insets` is what the track's frame takes from its width; the
 *   label's line sits `gap` under the track, and a count after its label keeps `countGap` from it.
 * - A flag mark's box holds its locked variant at either size (`flagRow`).
 * - The reading panel is `READING_WIDTH` by `READING_HEIGHT` for every text (ADR-077), the window frame's padding around its
 *   text and its button, `gap` apart.
 */
export const KIT_METRICS = {
  button: { height: framedHeight("button", BUTTON_LINE), paddingX: space(8) },
  iconButton: { width: framedWidth("button", ICON_SIZES.button), height: ICON_BUTTON_HEIGHT },
  busy: { dot: space(2), gap: space(2), width: BUSY_WIDTH, buttonTop: centreOffset(BUTTON_LINE, space(2)), iconTop: centreOffset(ICON_SIZES.button, space(2)), iconLeft: centreOffset(ICON_SIZES.button, BUSY_WIDTH) },
  field: { head: FIELD_HEAD, boxHeight: FIELD_BOX, height: FIELD_HEAD + FIELD_HEAD_GAP + FIELD_BOX, headGap: FIELD_HEAD_GAP, noteGap: FIELD_NOTE_GAP, paddingX: space(4), paddingY: FIELD_PADDING_Y, reveal: { box: FIELD_INNER, glyphOffset: centreOffset(FIELD_INNER, GLYPH_SIZES.eye), right: space(4) - centreOffset(FIELD_INNER, GLYPH_SIZES.eye) }, search: { gap: space(3) } },
  note: { height: NOTE_LINE, textOffset: centreOffset(NOTE_LINE, BODY_TYPE.caption.lineHeight), glyphOffset: centreOffset(NOTE_LINE, GLYPH_SIZES.alert), gap: space(3) },
  buttonGlyph: { gap: space(4), caretGap: space(3), top: centreOffset(BUTTON_LINE, GLYPH_SIZES.reload) },
  badge: { height: BADGE_HEIGHT, paddingX: space(2), offset: space(4) },
  check: { box: CHECK_BOX, row: CHECK_ROW, boxOffset: centreOffset(CHECK_ROW, CHECK_BOX), labelOffset: centreOffset(CHECK_ROW, LABEL_LINE), gap: space(4) },
  segmented: { gap: space(1) },
  key: { height: KEY_HEIGHT, paddingX: space(3) },
  window: { header: WINDOW_HEADER, titleOffset: centreOffset(WINDOW_HEADER, LABEL_LINE), titleRow: WINDOW_TITLE_ROW, titleRowOffset: centreOffset(WINDOW_TITLE_ROW, LABEL_LINE), padding: space(6), headerPaddingLeft: space(4), footer: { gap: space(4), top: space(4), side: space(8), bottom: 7 } },
  panel: { padding: space(4) },
  hud: { padding: space(3) },
  divider: { thickness: space(2), margin: space(4) },
  dialog: { header: DIALOG_HEADER, title: TITLE_LINE, titleOffset: centreOffset(DIALOG_HEADER, TITLE_LINE), padding: space(8), gap: space(6), hintGap: space(4), hintItemGap: space(3) },
  message: { height: MESSAGE_LINE, paddingX: MESSAGE_PADDING_X, paddingY: MESSAGE_PADDING_Y, chromeX: framedWidth("notice", MESSAGE_PADDING_X + MESSAGE_PADDING_X) },
  notice: { padding: space(8), gap: space(4), detailPaddingX: space(4), detailPaddingY: space(2), actionsGap: space(6) },
  menu: { item: MENU_ITEM, paddingX: MENU_PADDING_X, padding: space(1), glyphGap: MENU_GLYPH_GAP, dividerMargin: space(2) },
  list: { row: LIST_ROW, edge: ROW_EDGE, paddingX: space(4), paddingY: ROW_PADDING, glyph: ROW_GLYPH, glyphGap: space(4), trail: LABEL_LINE, subGap: ROW_SUB_GAP, name: ROW_NAME, sub: ROW_SUB, heading: space(3) + LABEL_LINE + space(1), headingTop: space(3) },
  empty: { paddingX: space(8), paddingY: space(12), gap: space(3) },
  bar: { fill: BAR_FILL, track: BAR_TRACK, insets: BAR_INSETS, gap: space(3), countGap: space(4) },
  flag: { 16: flagRow(16), 8: flagRow(8) },
  reading: { width: READING_WIDTH, height: READING_HEIGHT, padding: space(6), gap: space(4) },
} as const;

/**
 * A list row's height in logical pixels: its edge and padding on each side around its content (`listRowContent`).
 *
 * @param row Whether the row leads with a glyph, carries a sub-line and ends with a trailing value, and the height of
 *   the picture it leads with in the glyph's place, when it has one.
 * @returns The row's height.
 * @example
 * listRowHeight({ glyph: false, sub: false, trailing: false });
 * // 14
 */
export function listRowHeight(row: { readonly glyph: boolean; readonly sub: boolean; readonly trailing: boolean; readonly lead?: number }): number {
  return ROW_EDGE + ROW_PADDING + listRowContent(row) + ROW_PADDING + ROW_EDGE;
}

/**
 * The height of a list row's content in logical pixels: the tallest of its glyph or the picture it leads with, its
 * text and its trailing value's line in the pixel face, each of which the row sets in the middle of it.
 *
 * @param row Whether the row leads with a glyph, carries a sub-line and ends with a trailing value, and the height of
 *   the picture it leads with, when it has one.
 * @returns The content's height.
 */
export function listRowContent(row: { readonly glyph: boolean; readonly sub: boolean; readonly trailing: boolean; readonly lead?: number }): number {
  const text = row.sub ? ROW_NAME + ROW_SUB_GAP + ROW_SUB : ROW_NAME;
  return Math.max(text, row.glyph ? ROW_GLYPH : 0, row.trailing ? LABEL_LINE : 0, row.lead ?? 0);
}

/**
 * Where a menu item's parts sit, in logical pixels. Every item of a menu is as tall as the tallest part any of them
 * carries: the label's line with a pixel above and below, or the key glyph when an item of the menu carries a key
 * hint (Resume's Esc on the game menu), so the items stay one height. The label, the leading glyph and the key
 * glyph are each set in the middle of the item, and a busy item's dots in the middle of the label's line, starting
 * where its label does.
 *
 * @param menu Whether any item of the menu carries a key hint.
 * @param item The size of the glyph this item leads with, or null when it has none.
 * @returns The item's height, the tops of its label, its glyph, its reason (the caption's line), its key glyph and
 *   its busy dots, and the busy dots' left edge inside the item's padding box.
 * @example
 * menuItemLayout({ hinted: false }, { glyph: null });
 * // { height: 15, labelTop: 1, glyphTop: 0, whyTop: 3, keyTop: 0, busyTop: 6, busyLeft: 4 }
 */
export function menuItemLayout(menu: { readonly hinted: boolean }, item: { readonly glyph: number | null }): { readonly height: number; readonly labelTop: number; readonly glyphTop: number; readonly whyTop: number; readonly keyTop: number; readonly busyTop: number; readonly busyLeft: number } {
  const height = menu.hinted ? Math.max(MENU_ITEM, KEY_HEIGHT) : MENU_ITEM;
  const labelTop = centreOffset(height, LABEL_LINE);
  return {
    height,
    labelTop,
    glyphTop: item.glyph === null ? 0 : centreOffset(height, item.glyph),
    whyTop: centreOffset(height, BODY_TYPE.caption.lineHeight),
    keyTop: menu.hinted ? centreOffset(height, KEY_HEIGHT) : 0,
    busyTop: labelTop + centreOffset(LABEL_LINE, space(2)),
    busyLeft: MENU_PADDING_X + (item.glyph === null ? 0 : item.glyph + MENU_GLYPH_GAP),
  };
}

/**
 * How far below the top of a button's label line a glyph of the given size starts, so a glyph of any size sits in
 * the middle of the line: 0 for the 12 px reload, 2 for the 8 px play and logout (with the button role's line of 13).
 *
 * @param size The glyph's size in logical pixels.
 * @returns The offset in logical pixels.
 * @throws RangeError when the size is not a whole number.
 */
export function buttonGlyphTop(size: number): number {
  return centreOffset(BUTTON_LINE, size);
}
